import React, { useState } from 'react';

import { ArrowLeft, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';
import { PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS } from './constants/types';

import {
    GetAllProductsQuery,
    ProductArrangementReassortmentType,
    ProductArrangementWithProductsFragmentFragment,
    useUpdateProductArrangementMutation,
    TotadminPermission,
} from 'data/__generated__';

import { FullPageLoader } from 'components/FullPageLoader';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemInput } from 'components/TotemInput';
import { TotemSelect } from 'components/TotemSelect';
import { ProductArrangementProductSelector } from './ProductArrangementProductSelector';
import { ProductsArrangementProductsTable } from './ProductsArrangementProductsTable';

import {
    mapProductArrangementFormValuesToProductArrangementUpdateInput,
    mapProductArrangementToProductArrangementFormValues,
    ProductArrangementFormValues,
} from './FormHelper/DataMapper';

export const ProductArrangementDetailsForm = ({
    productArrangement,
    products,
}: {
    productArrangement: ProductArrangementWithProductsFragmentFragment;
    products: GetAllProductsQuery['getAllProducts'];
}) => {
    const [productArrangementFormValues, setProductArrangementFormValues] = useState<ProductArrangementFormValues>(
        mapProductArrangementToProductArrangementFormValues({ productArrangement }),
    );
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

    const [updateProductArrangement, { loading: updateLoading }] = useUpdateProductArrangementMutation();

    function setProductArrangementFormValuesAndIsFormDirty({
        newProductArrangementFormValues,
        newIsDirty = true,
    }: {
        newProductArrangementFormValues: ProductArrangementFormValues;
        newIsDirty?: boolean;
    }) {
        setProductArrangementFormValues(newProductArrangementFormValues);
        setIsFormDirty(newIsDirty);
    }

    function resetFormValues() {
        setProductArrangementFormValuesAndIsFormDirty({
            newProductArrangementFormValues: mapProductArrangementToProductArrangementFormValues({
                productArrangement,
            }),
            newIsDirty: false,
        });
    }

    async function onSubmit(e?: React.FormEvent<HTMLFormElement>) {
        e?.preventDefault();

        const productArrangementUpdateInput = mapProductArrangementFormValuesToProductArrangementUpdateInput({
            productArrangementFormValues,
        });
        const { data } = await updateProductArrangement({
            variables: { productArrangement: productArrangementUpdateInput },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la modification de la gamme de produit');
        }

        const {
            updateProductArrangementMutation: { newProductArrangement, errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        if (newProductArrangement) {
            toast.success(`La gamme de produit ${newProductArrangement.name} a bien été modifié !`);
            setProductArrangementFormValuesAndIsFormDirty({
                newProductArrangementFormValues: mapProductArrangementToProductArrangementFormValues({
                    productArrangement: newProductArrangement,
                }),
                newIsDirty: false,
            });
        }
    }

    const reassortmentTypeOptions = Object.keys(ProductArrangementReassortmentType)
        .map((type) => ({
            value: type as ProductArrangementReassortmentType,
            label: PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS[type as ProductArrangementReassortmentType],
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Gamme de produit',
            items: [
                {
                    label: 'Réinitialiser',
                    icon: <RotateCcw />,
                    onClick: resetFormValues,
                    disabled: !isFormDirty,
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/product-arrangements' }],
        },
    ];

    return (
        <>
            <Form onSubmit={onSubmit}>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.productArrangementDetails} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={actionGroups}
                        onSave={onSubmit}
                        saveDisabled={
                            !isFormDirty ||
                            productArrangementFormValues.productsWithStockMax.some(
                                ({ stockMax }) => !Number.isInteger(stockMax),
                            )
                        }
                        saveTitle="Mettre à jour"
                        saveRequiredPermissions={[TotadminPermission.ProductArrangementsManage]}
                    />
                </Header>
                <Content>
                    <TotemInput
                        label="Nom de la gamme de produit"
                        placeholder="Dessert Bergam laitier"
                        onChange={(newName) =>
                            setProductArrangementFormValuesAndIsFormDirty({
                                newProductArrangementFormValues: { ...productArrangementFormValues, name: newName },
                            })
                        }
                        value={productArrangementFormValues.name}
                    />
                    <TotemSelect
                        placeholder="Sélectionner un type de réassort"
                        value={productArrangementFormValues.reassortmentType}
                        options={reassortmentTypeOptions}
                        onChange={(value) => {
                            if (value) {
                                setProductArrangementFormValuesAndIsFormDirty({
                                    newProductArrangementFormValues: {
                                        ...productArrangementFormValues,
                                        reassortmentType: value,
                                    },
                                });
                            }
                        }}
                    />
                    <ProductArrangementProductSelector
                        productArrangementFormValues={productArrangementFormValues}
                        setProductArrangementFormValuesAndIsFormDirty={setProductArrangementFormValuesAndIsFormDirty}
                        products={products}
                    />
                    <ProductsArrangementProductsTable
                        productArrangementFormValues={productArrangementFormValues}
                        setProductArrangementFormValuesAndIsFormDirty={setProductArrangementFormValuesAndIsFormDirty}
                        products={products}
                    />
                </Content>
            </Form>

            {updateLoading ? <FullPageLoader /> : null}
        </>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    width: 100%;
    flex-grow: 1;
    padding: 15px;
    overflow: scroll;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
